import { cn } from '@shared/utils';
import type { ChartSegment } from './chart-colors';

export interface SegmentedBarProps {
  /** 各段依陣列順序由左排到右；值為 0 的段不畫 */
  segments: ChartSegment[];
  /** 圖例與無障礙描述裡的數值文字 */
  formatValue: (value: number) => string;
  /**
   * 上限（例如每日目標）。有值時以上限為滿格，總量未達上限的部分留白；
   * 總量超過上限時改以總量為滿格，並在上限的位置畫一道刻度線，右側就是超出的量。
   */
  limit?: number;
  /** 整條圖的無障礙描述；省略時由各段的「標籤 數值」組成 */
  ariaLabel?: string;
  size?: 'sm' | 'md';
  /** `grid`：圖例逐項對齊、數值靠右；`inline`：圖例接成一行，適合空間小的卡片 */
  legend?: 'grid' | 'inline';
  className?: string;
}

/**
 * 分段比例條：一條橫條依各段數值切開，下方附圖例。
 *
 * 用橫條而非圓餅：部分對整體的比較，長度比角度好讀，也不必另留一塊正方形版面。
 * 圖例一律附數值文字，因為部分分類色對背景低於 3:1，數字是顏色以外的辨識線索。
 */
export function SegmentedBar({
  segments,
  formatValue,
  limit,
  ariaLabel,
  size = 'md',
  legend = 'grid',
  className,
}: SegmentedBarProps) {
  const visibleSegments = segments.filter((segment) => segment.value > 0);
  if (visibleSegments.length === 0) return null;

  const total = visibleSegments.reduce(
    (sum, segment) => sum + segment.value,
    0,
  );
  const scale = limit === undefined ? total : Math.max(total, limit);
  const describe = (segment: ChartSegment) =>
    `${segment.label} ${formatValue(segment.value)}`;

  return (
    <figure className={cn('flex flex-col gap-2.5', className)}>
      <div
        /* 段與段之間留 2px，分界不必全靠顏色：光看斷點就數得出有幾段 */
        className={cn(
          'relative flex gap-0.5 overflow-hidden rounded-full bg-foreground/5',
          size === 'sm' ? 'h-1.5' : 'h-2.5',
        )}
        role="img"
        aria-label={ariaLabel ?? visibleSegments.map(describe).join('、')}
      >
        {visibleSegments.map((segment, index) => (
          <span
            key={segment.key}
            title={describe(segment)}
            className={cn(
              'h-full',
              index === 0 && 'rounded-l-full',
              index === visibleSegments.length - 1 && 'rounded-r-full',
              segment.colorClass,
            )}
            style={{ width: `${(segment.value / scale) * 100}%` }}
          />
        ))}

        {/*
          超過上限後條已填滿，光看長度分不出「剛好」與「超過」。刻度線標出上限的位置，
          各段仍保留自己的顏色，不必把整條翻紅。
        */}
        {limit !== undefined && total > limit && (
          <span
            className="absolute inset-y-0 w-0.5 bg-error"
            style={{ left: `${(limit / scale) * 100}%` }}
          />
        )}
      </div>

      <figcaption>
        {legend === 'grid' ? (
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
            {visibleSegments.map((segment) => (
              <li
                key={segment.key}
                className="flex min-w-0 items-center gap-2 text-xs"
              >
                <LegendDot colorClass={segment.colorClass} />
                <span className="truncate text-foreground">
                  {segment.label}
                </span>
                <span className="ml-auto font-mono text-[0.6875rem] tabular-nums text-muted-foreground">
                  {formatValue(segment.value)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[0.625rem] tracking-wide text-muted-foreground">
            {visibleSegments.map((segment) => (
              <li key={segment.key} className="flex items-center gap-1.5">
                <LegendDot colorClass={segment.colorClass} />
                {describe(segment)}
              </li>
            ))}
          </ul>
        )}
      </figcaption>
    </figure>
  );
}

/* 8px：色點是圖例裡唯一對得上段落的線索，太小就認不出是哪一段 */
function LegendDot({ colorClass }: { colorClass: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('h-2 w-2 shrink-0 rounded-full', colorClass)}
    />
  );
}
